import type {
    Application,
    ApplicationInfo,
    ApplicationSession,
    ApplicationsListQuery,
    ApplicationVersion,
    ApplicationVersionDetails,
    CopyApplicationRequest,
    CopyApplicationResponse,
    CreateApplicationRequest,
    CreateSessionResponse,
    CreateVersionRequest,
    MessageAttachment,
    Resource,
    VersionInfo,
    SessionLog, AssetDetails, UpdateAssetRequest, TenantPrompt, UpdateTenantPromptRequest, ApplicationRole,
    RoleAssignment, UpdateRoleAssignmentsRequest, SessionBuildLog, PreviewDevice, CreatePreviewDeviceRequest,
    Feedback, McpServer, CreateMcpServerRequest, UpdateMcpServerRequest, McpProbeResult
} from '../types/ai-apps';
import { defaultBuildParameters } from '../utils/applications';

import { getBaseUrl } from 'vince-core/utils/env';
import { APIClient } from 'vince-core/api/apiClient';
import type { UsagePayload, UsageSummary } from 'vince-core/types/ai';

export const baseUrl = getBaseUrl('/v1/ai-apps');

export const loadTenantPrompt = async (): Promise<TenantPrompt | undefined> => {
    const response = await APIClient.raw<TenantPrompt>(`${baseUrl}/tenant-prompt`);

    if (response.status === 200) {
        return response._data;
    } else if (response.status === 204) {
        return undefined;
    }

    return response._data;
};

export const updateTenantPrompt = async (req: UpdateTenantPromptRequest): Promise<void> => {
    await APIClient(`${baseUrl}/tenant-prompt`, {
        method: 'POST',
        body: JSON.stringify(req)
    });
};

export const loadMcpServers = async (): Promise<McpServer[]> => {
    return await APIClient<McpServer[]>(`${baseUrl}/mcp-servers`);
};

export const createMcpServer = async (req: CreateMcpServerRequest): Promise<McpServer> => {
    return await APIClient<McpServer>(`${baseUrl}/mcp-servers`, {
        method: 'POST',
        body: JSON.stringify(req)
    });
};

export const updateMcpServer = async (mcpServerId: string, req: UpdateMcpServerRequest): Promise<McpServer> => {
    return await APIClient<McpServer>(`${baseUrl}/mcp-servers/${mcpServerId}`, {
        method: 'PATCH',
        body: JSON.stringify(req)
    });
};

export const deleteMcpServer = async (mcpServerId: string): Promise<void> => {
    await APIClient<void>(`${baseUrl}/mcp-servers/${mcpServerId}`, { method: 'DELETE' });
};

export const probeMcpServer = async (config: string): Promise<McpProbeResult> => {
    return await APIClient<McpProbeResult>(`${baseUrl}/mcp-servers/probe`, {
        method: 'POST',
        body: JSON.stringify({ config })
    });
};

export const loadApplications = (query: ApplicationsListQuery): Promise<Application[]> => {
    const { rowsPerPage, page, ...rest } = query;

    const params = new URLSearchParams();
    Object.entries(rest).forEach((it) => {
        if (it[1] !== undefined) {
            params.set(it[0], String(it[1]));
        }
    });

    const hasValidPaging =
        Number.isInteger(rowsPerPage) && (rowsPerPage as number) > 0 &&
        Number.isInteger(page) && (page as number) > 0;

    if (hasValidPaging) {
        params.set('rowsPerPage', String(rowsPerPage));
        params.set('page', String(page));
    }

    return APIClient<Application[]>(`${baseUrl}/?${params.toString()}`);
};

export const deleteApplication = async (applicationId: string) => {
    await APIClient<Application[]>(`${baseUrl}/${applicationId}`, { method: 'DELETE' });
};

export const copyApplication = async (applicationId: string, payload: CopyApplicationRequest) => {
    return await APIClient<CopyApplicationResponse>(`${baseUrl}/${applicationId}/copy`, {
        method: 'POST',
        body: JSON.stringify(payload)
    });
};

export const loadApplicationsLabels = async (): Promise<string[]> => {
    return await APIClient<string[]>(`${baseUrl}/labels`);
};

export const createApplication = async (application: CreateApplicationRequest): Promise<Application> => {
    return await APIClient<Application>(`${baseUrl}/`, { method: 'POST', body: JSON.stringify(application) });
};

export const loadApplicationVersions = async (applicationId: string): Promise<ApplicationVersion[]> => {
    return await APIClient<ApplicationVersion[]>(`${baseUrl}/${applicationId}/versions`);
};

export const loadApplicationDetails = async (applicationId: string): Promise<ApplicationInfo> => {
    return await APIClient<ApplicationInfo>(`${baseUrl}/${applicationId}`);
};

export const loadTenantBuildUsage = async (): Promise<UsageSummary> => {
    return await APIClient<UsageSummary>(`${baseUrl}/build-usage`);
};

export const loadApplicationBuildUsage = async (applicationId: string): Promise<UsageSummary> => {
    return await APIClient<UsageSummary>(`${baseUrl}/${applicationId}/build-usage`);
};

export const loadVersionBuildUsage = async (applicationId: string, versionId: string): Promise<UsageSummary> => {
    return await APIClient<UsageSummary>(`${baseUrl}/${applicationId}/versions/${versionId}/build-usage`);
};

export const loadSessionBuildUsage = async (
    applicationId: string,
    versionId: string,
    sessionId: string
): Promise<UsagePayload | null> => {
    const fetchOptions = {
        ignoreResponseError: true,
        silent: true
    };
    const response = await APIClient.raw<UsagePayload>(
        `${baseUrl}/${applicationId}/versions/${versionId}/build-sessions/${sessionId}/build-usage`,
        fetchOptions
    );

    if (response.status === 404) {
        return null;
    }
    if (!response.ok) {
        throw new Error(`Failed to load build usage (${response.status})`);
    }

    return response._data ?? null;
};

export const startBuildSession = async (
    applicationId: string,
    versionId: string,
    metadata?: Record<string, string>
): Promise<CreateSessionResponse> => {
    const buildParams = await defaultBuildParameters();
    const sessionMetadata = Object.assign(buildParams, metadata || {});

    return await APIClient<CreateSessionResponse>(
        `${baseUrl}/${applicationId}/versions/${versionId}/build-sessions`,
        {
            method: 'POST',
            body: JSON.stringify({ metadata: sessionMetadata })
        }
    );
};

export const updateApplicationInfo = async (applicationInfo: ApplicationInfo): Promise<ApplicationInfo> => {
    return await APIClient<ApplicationInfo>(`${baseUrl}/${applicationInfo.id}`, {
        method: 'PATCH',
        body: JSON.stringify({
            name: applicationInfo.name,
            description: applicationInfo.description,
            labels: applicationInfo.labels
        })
    });
};

export const updateVersionInfo = async (applicationId: string, versionId: string, applicationInfo: VersionInfo): Promise<ApplicationInfo> => {
    return await APIClient<ApplicationInfo>(`${baseUrl}/${applicationId}/versions/${versionId}`, {
        method: 'PATCH',
        body: JSON.stringify(applicationInfo)
    });
};

export const loadVersionDetails = async (applicationId: string, versionId: string): Promise<ApplicationVersionDetails> => {
    return await APIClient<ApplicationVersionDetails>(`${baseUrl}/${applicationId}/versions/${versionId}`);
};

export const createVersion = async (applicationId: string, version: CreateVersionRequest): Promise<ApplicationVersion> => {
    return await APIClient<ApplicationVersion>(`${baseUrl}/${applicationId}/versions`, { method: 'POST', body: JSON.stringify(version) });
};

export const startApplicationRunSession = async (applicationId: string, versionId: string | undefined = undefined): Promise<ApplicationSession> => {
    const endpoint = versionId !== undefined
        ? `${baseUrl}/${applicationId}/versions/${versionId}/sessions`
        : `${baseUrl}/${applicationId}/sessions`;

    return await APIClient<ApplicationSession>(endpoint, { method: 'POST' });
};

export const deleteApplicationRunSession = async (applicationId: string, sessionId: string): Promise<void> => {
    return await APIClient<void>(`${baseUrl}/${applicationId}/sessions/${sessionId}`, { method: 'DELETE' });
};

export const dropBuildSessionSession = async (applicationId: string, versionId: string, sessionId: string): Promise<void> => {
    return await APIClient<void>(`${baseUrl}/${applicationId}/versions/${versionId}/build-sessions/${sessionId}`, { method: 'DELETE' });
};

export const applyBuildSessionSession = async (applicationId: string, versionId: string, sessionId: string): Promise<void> => {
    return await APIClient<void>(`${baseUrl}/${applicationId}/versions/${versionId}/build-sessions/${sessionId}/apply`, { method: 'POST' });
};

export const updateResources = async (applicationId: string, versionId: string, resources: Resource[]): Promise<Resource[]> => {
    return await APIClient<Resource[]>(`${baseUrl}/${applicationId}/versions/${versionId}/resources`,
        {
            method: 'PATCH',
            body: JSON.stringify(resources)
        }
    );
};

export const sendMessage = async (
    applicationId: string,
    versionId: string,
    sessionId: string,
    content: string,
    attachments?: MessageAttachment[]
): Promise<{ turnIndex?: number }> => {
    return await APIClient<{ turnIndex?: number }>(
        `${baseUrl}/${applicationId}/versions/${versionId}/build-sessions/${sessionId}/message`,
        {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ content, attachments: attachments || [] })
        }
    );
};

export const undoLastPrompt = async (
    applicationId: string, versionId: string, sessionId: string, turnIndex: number
): Promise<void> => {
    await APIClient<void>(
        `${baseUrl}/${applicationId}/versions/${versionId}/build-sessions/${sessionId}/undo-last-prompt`,
        {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ turnIndex })
        }
    );
};

export const stopGeneration = async (applicationId: string, versionId: string, sessionId: string): Promise<void> => {
    await APIClient<void>(
        `${baseUrl}/${applicationId}/versions/${versionId}/build-sessions/${sessionId}/stop-generation`,
        { method: 'POST' }
    );
};

export const toggleFavourite = async (applicationId: string): Promise<void> => {
    return await APIClient<void>(`${baseUrl}/${applicationId}/toggle-favourite`, {
        method: 'POST'
    });
};

export const loadSessionLogs = async (
    applicationId: string,
    versionId: string,
    sessionId: string
): Promise<SessionLog[]> => {
    return await APIClient<SessionLog[]>(
        `${baseUrl}/${applicationId}/versions/${versionId}/build-sessions/${sessionId}/logs`
    );
};

export const loadAssetDetails = async (applicationId: string, versionId: string, assetId: string): Promise<AssetDetails> => {
    return await APIClient<AssetDetails>(`${baseUrl}/${applicationId}/versions/${versionId}/assets/${assetId}`);
};

export const updateAsset = async (applicationId: string, versionId: string, assetId: string, payload: UpdateAssetRequest): Promise<void> => {
    return await APIClient(
        `${baseUrl}/${applicationId}/versions/${versionId}/assets/${assetId}`,
        {
            method: 'POST',
            body: JSON.stringify(payload)
        }
    );
};

export const loadRoles = async (applicationId: string, versionId: string): Promise<ApplicationRole[]> => {
    return await APIClient<ApplicationRole[]>(`${baseUrl}/${applicationId}/versions/${versionId}/roles`);
};

export const saveRole = async (applicationId: string, versionId: string, role: Partial<ApplicationRole>): Promise<ApplicationRole> => {
    let url = `${baseUrl}/${applicationId}/versions/${versionId}/roles`;
    let payload = role;
    if (role.id !== undefined) {
        url += `/${role.id}`;
        payload = { description: role.description, role: role.role };
    }

    return await APIClient<ApplicationRole>(url, {
        method: role.id ? 'PATCH' : 'POST',
        body: JSON.stringify(payload)
    });
};

export const deleteRole = async (applicationId: string, versionId: string, roleId: string): Promise<ApplicationRole> => {
    return await APIClient<ApplicationRole>(`${baseUrl}/${applicationId}/versions/${versionId}/roles/${roleId}`, {
        method: 'DELETE'
    });
};

export const loadRoleAssignments = async (applicationId: string, versionId: string, roleId: string): Promise<RoleAssignment[]> => {
    return await APIClient<RoleAssignment[]>(`${baseUrl}/${applicationId}/versions/${versionId}/roles/${roleId}/assignments`);
};

export const updateRoleAssignments = async (applicationId: string, versionId: string, roleId: string, payload: UpdateRoleAssignmentsRequest): Promise<void> => {
    return await APIClient<void>(`${baseUrl}/${applicationId}/versions/${versionId}/roles/${roleId}/assignments`, {
        method: 'POST',
        body: JSON.stringify(payload)
    });
};

export const loadVersionBuildPrompt = async (applicationId: string, versionId: string): Promise<SessionBuildLog[]> => {
    return await APIClient<SessionBuildLog[]>(`${baseUrl}/${applicationId}/versions/${versionId}/build-prompt`);
};

export const loadPreviewDevices = async (): Promise<PreviewDevice[]> => {
    return await APIClient<PreviewDevice[]>(`${baseUrl}/preview-devices`);
};

export const savePreviewDevice = async (payload: CreatePreviewDeviceRequest): Promise<PreviewDevice> => {
    return await APIClient<PreviewDevice>(`${baseUrl}/preview-devices`, {
        method: 'POST',
        body: JSON.stringify(payload)
    });
};

export const deletePreviewDevice = async (previewDeviceId: string): Promise<void> => {
    await APIClient<PreviewDevice[]>(`${baseUrl}/preview-devices/${previewDeviceId}`, { method: 'DELETE' });
};

export const loadFeedback = async (
    applicationId: string,
    versionId: string | undefined = undefined,
    includeRead = false
): Promise<Feedback[]> => {
    const params = new URLSearchParams();
    if (versionId !== undefined) {
        params.set('versionId', versionId);
    }
    if (includeRead) {
        params.set('includeRead', 'true');
    }
    const query = params.toString();
    return await APIClient<Feedback[]>(`${baseUrl}/${applicationId}/feedback${query ? `?${query}` : ''}`);
};

export const markFeedbackAsRead = async (applicationId: string, feedbackId: string): Promise<void> => {
    return await APIClient<void>(`${baseUrl}/${applicationId}/feedback/${feedbackId}/mark-as-read`, {
        method: 'POST'
    });
};

export const submitRunSessionFeedback = async (
    applicationId: string,
    sessionId: string,
    comment: string
): Promise<Feedback> => {
    return await APIClient<Feedback>(`${baseUrl}/${applicationId}/sessions/${sessionId}/feedback`, {
        method: 'POST',
        body: JSON.stringify({ comment })
    });
};
